import type {
  ComponentProps,
  ReactNode,
} from 'react'

import {
  Breadcrumb as AriaBreadcrumb,
  Breadcrumbs as AriaBreadcrumbs,
} from 'react-aria-components/Breadcrumbs'

import {
  useTranslation,
} from 'react-i18next'

import {
  ChevronRight,
} from 'lucide-react'

import { cn } from '@/shared/lib/cn'

type AriaBreadcrumbsProps =
  ComponentProps<
    typeof AriaBreadcrumbs
  >

type AriaBreadcrumbProps =
  ComponentProps<
    typeof AriaBreadcrumb
  >

export interface BreadcrumbsProps
  extends Omit<
    AriaBreadcrumbsProps,
    'children' | 'className'
  > {
  children: ReactNode
  ariaLabel?: string
  className?: string
  listClassName?: string
}

export function Breadcrumbs({
  children,
  ariaLabel,
  className,
  listClassName,
  ...props
}: BreadcrumbsProps) {
  const { t } = useTranslation()

  return (
    <nav
      aria-label={
        ariaLabel ??
        t('ui.breadcrumbs.label')
      }
      className={cn(
        'min-w-0',
        className,
      )}
    >
      <AriaBreadcrumbs
        {...props}
        className={cn(
          [
            'flex min-w-0',
            'flex-wrap items-center',
            'gap-y-1',
          ],
          listClassName,
        )}
      >
        {children}
      </AriaBreadcrumbs>
    </nav>
  )
}

export interface BreadcrumbItemProps
  extends Omit<
    AriaBreadcrumbProps,
    'children' | 'className'
  > {
  children: ReactNode
  className?: string
}

export function BreadcrumbItem({
  children,
  className,
  ...props
}: BreadcrumbItemProps) {
  return (
    <AriaBreadcrumb
      {...props}
      className={cn(
        [
          'flex min-w-0',
          'items-center gap-2',
          'text-sm',
          'text-(--color-muted-foreground)',
          '[&:first-child>svg]:hidden',

          'data-current:font-medium',
          'data-current:text-(--color-foreground)',

          'data-disabled:opacity-50',

          '[&_a]:rounded-sm',
          '[&_a]:font-medium',
          '[&_a]:outline-none',
          '[&_a]:transition-colors',
          '[&_a]:hover:text-(--color-foreground)',
          '[&_a]:focus-visible:ring-2',
          '[&_a]:focus-visible:ring-(--color-primary)',
          '[&_a]:focus-visible:ring-offset-2',
        ],
        className,
      )}
    >
      {({ isCurrent }) => (
        <>
          <ChevronRight
            aria-hidden="true"
            className="h-4 w-4 shrink-0 rtl:rotate-180"
          />

          {isCurrent ? (
            <span
              aria-current="page"
              className="truncate"
            >
              {children}
            </span>
          ) : (
            <span className="min-w-0 truncate">
              {children}
            </span>
          )}
        </>
      )}
    </AriaBreadcrumb>
  )
}